/* eslint-disable import/no-unused-modules */

export const PAYMENT_PROCESS_TIMEOUT_MESSAGE =
    "We couldn't connect to the payment service. Please try again.";
export const PAYMENT_SUCCESS_MESSAGE = 'Payment completed successfully!';
export const PAYMENT_ALREADY_COMPLETED_MESSAGE =
    'Payment has already been completed';

export type Currency = 'NZD' | 'USD' | 'AUD'; // Only supported currencies
export const CurrencyValues = ['NZD', 'USD', 'AUD'];

export type InvoiceStatus =
    | 'Draft'
    | 'Sent'
    | 'Expired'
    | 'Cancelled'
    | 'Accepted'
    | 'Issued'
    | 'PartialPaid'
    | 'Paid'
    | 'Overdue'
    | 'Refunded';

export type InvoicePaymentStatus =
    | 'Upcoming'
    | 'Overdue'
    | 'FullyPaid'
    | 'Refunded'
    | 'Canceled';

/**
 * Interface for product items in ecommerce
 */
export interface ProductItem {
    /** Unique identifier for the product */
    id: number;
    /** Product name */
    name: string;
    /** Detailed product description */
    description: string;
    /** URL to product image */
    imageUrl: string;
    /** Quantity */
    quantity: number;
    /** Original unit price in cents, before tax */
    unitPrice: number;
    /** Discounted price in cents (0 if no discount), before tax */
    discountedUnitPrice: number;
    /** Discount amount per unit in cents (0 if no discount) */
    discountAmountPerUnit: number;
    /** Whether the product has a discount */
    discountApplied: boolean;
    /** Tax Rate, eg 0.15 for 15% */
    taxRate: number;
    /** Final price after discounts in cents, before tax */
    totalPrice: number;
    /** Final price after discounts and tax in cents */
    totalPriceAfterTax: number;
    currency: Currency;
}

/**
 * Interface for payment items in ecommerce
 */
export interface PaymentItem {
    /** Unique identifier for the payment */
    id: number;
    /** Payment name/description */
    name: string;
    /** Payment status */
    status: InvoicePaymentStatus;
    /** Timestamp of due date */
    dueDate: number | null;
    /** Timestamp when payment was made (null if not paid) */
    paymentDate: number | null;
    /** Payment amount in cents */
    amount: number;
    /** Payment currency */
    currency: Currency;
}

export interface ProfessionalInfo {
    title: string;
    subTitle: string;
    address: string;
    city: string;
    postCode: string;
    state: string;
    bn: string; // ABN or NZBN
}

export interface Contact {
    id: number;
    fullName: string;
    email: string;
    firstName?: string;
    lastName?: string;
    companyName?: string;
    phone?: string;
    address?: string;
    postCode?: string;
    city?: string;
    hasStripePaymentMethod?: boolean;
}

export interface ProposalTemplate {
    headline: string;
    subHeadline: string;
    footerLinks: { title: string; url: string }[];
    note: string;
    contact: Contact;
    /** Rendered HTML for the contact block (tokenised field), same pattern as accountDetails */
    contactInfo?: string;
    accountDetails?: string;
    scope?: string;
    paymentTerms?: string;
    creditCardTerms?: string;
    bankTransferTerms?: string;
    references?: string;
    /** Business terms from BusinessTermsVersion (title, body content, and URL) */
    businessTerms: { title: string; body: string; url: string }[];
}

export interface Proposal {
    id: number;
    urlSegment: string;
    uuid: string;
    docType: 'quote' | 'invoice';
    isSplitPayment: boolean;
    professionalInfo: ProfessionalInfo;
    proposalTemplate: ProposalTemplate;
    productItems: ProductItem[];
    paymentItems: PaymentItem[];
    status: InvoiceStatus;
    currency: Currency;
    discounts: number;
    subtotal: number;
    tax: number;
    total: number;
    summaries: string[];
    customer: Contact;
    expiryDate: number | null;
    /** New-business pipeline: ExpiryDate is null until first send; the editor may project today + 14. */
    expiryAutoSetOnSend?: boolean;
    invoiceSentDate: number | null;
    createdAt: number | null;
    updatedAt: number | null;
    stripeHostedInvoiceUrl: string | null;
    stripeAccount: string | null;
    /** Stripe publishable key for this invoice's Stripe account */
    publishableKey: string | null;
    hasStripePaymentMethod: boolean;
    /** Whether the invoice has a payment currently being processed */
    hasProcessingPayment: boolean;
    poNumber?: string;
    /** Invoice/quote number. For quotes, may exist (e.g. from HubSpot) — intentional, not a bug. */
    invoiceNumber?: string;
    startDate?: string;
    endDate?: string;
    /** Server-rendered membership term (from Invoice.MembershipTerm). */
    membershipTerm: string;
    /** When true, omit membership term in proposal UI (New Business pipeline quotes). */
    hideMembershipTerm?: boolean;
    /** Signature data from when the quote was accepted */
    signature?: {
        fullName: string;
        email: string;
        dataUrl: string;
        signedDate: string;
    };
    /** Deal owner (team member) assigned from HubSpot */
    dealOwner?: {
        id: string;
        name: string;
        email?: string;
        phone?: string;
        bookingUrl?: string;
        avatarUrl?: string;
    };
}

export interface SurchargeConfig {
    /** Surcharge rate as decimal (e.g. 0.027 for 2.7%) */
    rate: number;
    flatFee: number;
}

/** Describes a single column in a product or payment table. */
export interface ColumnDef {
    key: string;
    header: string;
    /** Tailwind classes for column cells (width, alignment, etc.) */
    className?: string;
}
